import { createAction, createSelector } from '@reduxjs/toolkit';
import {
  AlarmCall,
  AlarmCallEvent,
  AlarmCallParticipant,
  AlarmCallType,
  AlarmLogActionCallKind,
  AlarmLogEvent,
  AlarmLogEventKind,
  AlarmLogProtocolKind,
} from '@x-guard/xgac-types/xgac';
import { uniqBy } from 'lodash';
import {
  ChatState,
  DispatchLogOperatorAction,
  LogOperatorActionArgs,
  Page,
  RS,
} from 'types';
import { AsyncStatus } from 'types/utils';
import {
  advancedQs, api, createSlice, createThunk, isAbortError,
} from 'lib';
import { alarmDetailsSelector } from 'features/details/exports';

const name = 'details/chat';

const heartbeatMs = 7000;

export const upsertCallEvent = createAction<AlarmCallEvent>(`${name}/upsertCallEvent`);
export const removeCallEvent = createAction<string>(`${name}/removeCallEvent`);
export const upsertParticipant = createAction<AlarmCallParticipant>(`${name}/upsertParticipant`);
export const removeParticipant = createAction<string>(`${name}/removeParticipant`);
export const upsertLogEvent = createAction<AlarmLogEvent>(`${name}/upsertLogEvent`);
export const addCallId = createAction<string>(`${name}/addCallId`);
export const closeHelperCall = createAction<string>(`${name}/closeHelperCall`);
// The alarm call (the alarm maker's own call) is kept apart from the helper chat: its events (recordings, positions) are not chat.
export const upsertAlarmCallEvent = createAction<AlarmCallEvent>(`${name}/upsertAlarmCallEvent`);
export const upsertAlarmCallParticipant = createAction<AlarmCallParticipant>(`${name}/upsertAlarmCallParticipant`);
export const removeAlarmCallEvent = createAction<string>(`${name}/removeAlarmCallEvent`);
// Older rows that arrive after the newest ones were shown: only rows we do not have yet are added.
export const appendChat = createAction<Partial<Pick<
  ChatState,
  'callEvents' | 'alarmCallEvents' | 'alarmCallParticipants' | 'logs' | 'operatorMessages'
>>>(`${name}/appendChat`);
export const finishBackfill = createAction(`${name}/finishBackfill`);

const initialState = {
  status: AsyncStatus.Idle,
  callEvents: [],
  participants: [],
  alarmCallEvents: [],
  alarmCallParticipants: [],
  operatorMessages: [],
  optimisticOperator: [],
  logs: [],
  callIds: [],
  openHelperCallIds: [],
  backfilling: false,
  error: null,
};

const upsertById = <T extends { _id: string }>(list: T[], item: T) => {

  const index = list.findIndex((row) => row._id === item._id);

  if (index === -1) {

    list.unshift(item);
    return;

  }

  list[index] = item;

};

export const readList = async <T>(url: string, signal: AbortSignal): Promise<T[]> => {

  const { data } = await api.get<Page<T> | T[]>(url, { signal });

  if (Array.isArray(data)) return data;

  return data.result ?? [];

};

// The api caps a page at 100; 50 keeps responses small.
const pageSize = 50;
// Pages after the first are fetched a few at a time instead of one after the other.
const pageConcurrency = 4;

const withoutDuplicates = <T>(rows: T[]) => uniqBy(rows, (row) => (row as { _id?: string })._id ?? row);

/** One page of a list endpoint. `url` must not set $max or $offset. */
const readPage = async <T>(url: string, signal: AbortSignal, offset: number): Promise<{ rows: T[]; total: number }> => {

  const { data } = await api.get<Page<T> | T[]>(
    `${url}${url.includes('?') ? '&' : '?'}${advancedQs({ $max: pageSize, $offset: offset })}`,
    { signal },
  );

  // An endpoint that does not page answers with a plain array.
  if (Array.isArray(data)) return { rows: data, total: data.length };

  const rows = data.result ?? [];

  return { rows, total: data.total ?? rows.length };

};

/** The pages after the first `loaded` rows, a few at a time; `onRows` gets every page as soon as it is in. */
const readRest = async <T>(url: string, signal: AbortSignal, loaded: number, total: number, onRows: (rows: T[]) => void) => {

  const offsets = Array.from({ length: Math.max(0, Math.ceil((total - loaded) / pageSize)) }, (_, index) => loaded + index * pageSize);

  let failure: PromiseRejectedResult | undefined;

  for (let start = 0; start < offsets.length && !signal.aborted && !failure; start += pageConcurrency) {

    // Settled, not rejected on the first failure: a failed page must not end the batch while its siblings still deliver rows.
    // eslint-disable-next-line no-await-in-loop
    const results = await Promise.allSettled(offsets.slice(start, start + pageConcurrency).map(async (offset) => {

      const { rows } = await readPage<T>(url, signal, offset);

      onRows(rows);

    }));

    failure = results.find((result): result is PromiseRejectedResult => result.status === 'rejected');

  }

  // A partial list must not pass for a complete one (readAll): the best-effort backfill settles this rejection itself.
  if (failure) throw failure.reason;

};

/** Every row of a list endpoint. `url` must not set $max or $offset. */
export const readAll = async <T>(url: string, signal: AbortSignal): Promise<T[]> => {

  const first = await readPage<T>(url, signal, 0);
  const rows = [...first.rows];

  await readRest<T>(url, signal, first.rows.length, first.total, (more) => rows.push(...more));

  // New rows between two requests shift the offset, so a row can come back on two pages.
  return withoutDuplicates(rows);

};

/** Names of asset groups by id; groups the user may not read are simply missing. */
export const loadAssetGroupNames = async (ids: string[], signal: AbortSignal): Promise<Map<string, string>> => {

  if (ids.length === 0) return new Map();

  const groups = await readAll<{ _id: string; name: string }>(`/asset-groups?${ids.map((id) => `_id=${id}`).join('&')}`, signal);

  return new Map(groups.map(({ _id, name: groupName }) => [_id, groupName]));

};

export const refreshLogEvent = createThunk<void, { alarmId: string; eventId: string }>({
  name: `${name}/refreshLogEvent`,
  handler: async ({ alarmId, eventId }, { dispatch }) => {

    const rows = await readList<AlarmLogEvent>(
      `/alarms/${alarmId}/alarm-log-events?${advancedQs({ _id: eventId, $max: 1 })}`,
      new AbortController().signal,
    );

    if (rows[0]) dispatch(upsertLogEvent(rows[0]));

  },
});

const isOperatorChat = (event: AlarmLogEvent): boolean => event.data.eventType === AlarmLogEventKind.OperatorChat;

const textOf = (meta: Record<string, unknown> | undefined, key: string) => {

  const value = meta?.[key];

  return typeof value === 'string' ? value : undefined;

};

const callKind = (action: string): AlarmLogActionCallKind => {

  switch (action) {

  case 'ACTION_CALL_RESULT_CANCELLED':
    return AlarmLogActionCallKind.ResultCancelled;
  case 'ACTION_CALL_RESULT_NOT_REACHABLE':
    return AlarmLogActionCallKind.ResultNotReachable;
  case 'ACTION_CALL_RESULT_NO_RESPONSE':
    return AlarmLogActionCallKind.ResultNoResponse;
  case 'ACTION_CALL_RESULT_RESPONDING':
    return AlarmLogActionCallKind.ResultResponding;
  default:
    return AlarmLogActionCallKind.Initiated;

  }

};

const operatorLogData = (args: LogOperatorActionArgs<any>): AlarmLogEvent['data'] => {

  const meta = args.meta as Record<string, unknown> | undefined;

  switch (args.action) {

  case 'ACTION_PROTOCOL_HELP':
    return { eventType: AlarmLogEventKind.Protocol, kind: AlarmLogProtocolKind.Help };
  case 'ACTION_PROTOCOL_VERIFICATION':
    return { eventType: AlarmLogEventKind.Protocol, kind: AlarmLogProtocolKind.Verification };
  case 'ACTION_SMS':
    return {
      eventType: AlarmLogEventKind.Sms,
      name: textOf(meta, 'name') ?? '',
      phone: textOf(meta, 'sms') ?? textOf(meta, 'number') ?? textOf(meta, 'phone') ?? '',
    };
  case 'ACTION_EMAIL':
    return {
      eventType: AlarmLogEventKind.Email,
      name: textOf(meta, 'name') ?? '',
      email: textOf(meta, 'email'),
    };
  default: {

    const targetAssetId = textOf(meta, 'targetAssetId');

    return {
      eventType: AlarmLogEventKind.ActionCall,
      name: textOf(meta, 'name') ?? '',
      phone: textOf(meta, 'phone') ?? textOf(meta, 'number') ?? '',
      kind: callKind(args.action),
      ...(targetAssetId ? {
        targetAsset: { _id: targetAssetId, _ref: 'Asset' as const },
      } : {}),
    };

  }

  }

};

export const logOperatorAction: DispatchLogOperatorAction = createThunk<void, LogOperatorActionArgs<any>>({
  name: `${name}/logOperatorAction`,
  handler: async (args, { getState }) => {

    const currentAlarm = alarmDetailsSelector(getState());

    await api.post(`/alarms/${currentAlarm._id}/alarm-log-events`, {
      data: operatorLogData(args),
    });

  },
});

type FetchChatArgs = {
  alarmId: string;
  controller: AbortController;
};

type FetchChatResult = {
  callEvents: AlarmCallEvent[];
  participants: AlarmCallParticipant[];
  alarmCallEvents: AlarmCallEvent[];
  alarmCallParticipants: AlarmCallParticipant[];
  operatorMessages: AlarmLogEvent[];
  logs: AlarmLogEvent[];
  callIds: string[];
  openHelperCallIds: string[];
};

const ignoreFailure = async <T>(signal: AbortSignal, read: () => Promise<T>, fallback: T): Promise<T> => {

  try {

    return await read();

  } catch (error) {

    if (signal.aborted || (error instanceof Error && isAbortError(error))) throw error;

    return fallback;

  }

};

/**
 * Shows the newest rows as soon as the first page of each list is in (everything is sorted newest first), and loads the
 * rest in the background: older rows are added through `appendChat`.
 */
export const fetchAlarmChat = createThunk<FetchChatResult, FetchChatArgs>({
  name,
  handler: async ({ alarmId, controller: { signal } }, { dispatch }) => {

    const calls = await readAll<AlarmCall>(`/alarms/${alarmId}/calls`, signal);
    const helperCalls = calls
      .filter((call) => call.type === AlarmCallType.HelperCall)
      .sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt));
    const callIds = helperCalls.map((call) => call._id);
    const openHelperCallIds = helperCalls.filter((call) => call.endedAt == null).map((call) => call._id);
    const newestFirst = `?${advancedQs({ $sort: '-createdAt' })}`;
    const eventsUrl = (callId: string) => `/alarm-calls/${callId}/alarm-call-events${newestFirst}`;
    const logsUrl = `/alarms/${alarmId}/alarm-log-events${newestFirst}`;
    const alarmCallIds = calls.filter((call) => call.type === AlarmCallType.AlarmCall).map((call) => call._id);

    const [eventPages, participantPages, logPage] = await Promise.all([
      Promise.all(callIds.map((callId) => ignoreFailure(signal, () => readPage<AlarmCallEvent>(eventsUrl(callId), signal, 0), null))),
      Promise.all(callIds.map((callId) => ignoreFailure(
        signal,
        () => readAll<AlarmCallParticipant>(`/alarm-calls/${callId}/alarm-call-participants`, signal),
        [],
      ))),
      readPage<AlarmLogEvent>(logsUrl, signal, 0),
    ]);

    if (callIds.length > 0 && eventPages.every((page) => page === null)) {

      throw new Error('Failed to load call events');

    }

    const backfill = async () => {

      // A page that arrives after the alarm was left belongs to the old alarm: drop it.
      const append = (rows: Parameters<typeof appendChat>[0]) => {

        if (!signal.aborted) dispatch(appendChat(rows));

      };
      const rest = <T>(url: string, first: { rows: T[]; total: number } | null, onRows: (rows: T[]) => void) => (
        first ? readRest<T>(url, signal, first.rows.length, first.total, onRows) : Promise.resolve()
      );
      // The alarm call (recordings, positions) is not chat: it is only read for the log, so it comes last and may stay empty.
      const alarmCall = async (callId: string) => {

        const url = `/alarm-calls/${callId}/alarm-call-events${newestFirst}`;
        const first = await ignoreFailure(signal, () => readPage<AlarmCallEvent>(url, signal, 0), null);

        if (!first) return;

        append({ alarmCallEvents: first.rows });
        await rest(url, first, (rows) => append({ alarmCallEvents: rows }));

      };

      // Known limit: when an older page fails, the log keeps what was loaded instead of showing an error for history.
      // Settled, so `finishBackfill` only runs when every request is done.
      await Promise.allSettled([
        ...callIds.map((callId, index) => rest(eventsUrl(callId), eventPages[index], (rows) => append({ callEvents: rows }))),
        rest(logsUrl, logPage, (rows) => append({
          logs: rows.filter((event) => !isOperatorChat(event)),
          operatorMessages: rows.filter(isOperatorChat),
        })),
        ...alarmCallIds.map(async (callId) => {

          const participants = await ignoreFailure(
            signal,
            () => readAll<AlarmCallParticipant>(`/alarm-calls/${callId}/alarm-call-participants`, signal),
            [],
          );

          append({ alarmCallParticipants: participants });
          await alarmCall(callId);

        }),
      ]);

      if (!signal.aborted) dispatch(finishBackfill());

    };

    // After this handler returns, so the rows it returns are in the store before older ones are added.
    window.setTimeout(backfill, 0);

    return {
      callIds,
      openHelperCallIds,
      callEvents: eventPages.flatMap((page) => page?.rows ?? []),
      participants: participantPages.flat(),
      alarmCallEvents: [],
      alarmCallParticipants: [],
      operatorMessages: logPage.rows.filter(isOperatorChat),
      logs: logPage.rows.filter((event) => !isOperatorChat(event)),
    };

  },
});

export const removeOptimisticOperator = createAction<string>(`${name}/removeOptimisticOperator`);

const dropOneOptimistic = (list: { message: string }[], message: string) => {

  const index = list.findIndex((row) => row.message === message);

  if (index >= 0) list.splice(index, 1);

};

export const appendOptimisticOperator = createAction<{
  _id: string;
  message: string;
  createdAt: string;
  name: string;
}>(`${name}/appendOptimisticOperator`);

export const chat = createSlice<ChatState, typeof name>({
  name,
  initialState,
  extraReducers: (builder) => builder
    .addCase(fetchAlarmChat.pending, (state) => {

      if (state.status.idle) state.status = AsyncStatus.Pending;

      state.error = null;

    })
    .addCase(fetchAlarmChat.fulfilled, (state, action) => {

      if (action.meta.arg.controller.signal.aborted) return;

      state.status = AsyncStatus.Fulfilled;
      const sameCalls = state.callIds.length === action.payload.callIds.length
        && state.callIds.every((id, index) => id === action.payload.callIds[index]);

      if (!sameCalls) state.callIds = action.payload.callIds;
      state.openHelperCallIds = action.payload.openHelperCallIds;
      state.callEvents = action.payload.callEvents;
      state.participants = action.payload.participants;
      state.alarmCallEvents = action.payload.alarmCallEvents;
      state.alarmCallParticipants = action.payload.alarmCallParticipants;
      state.operatorMessages = action.payload.operatorMessages;
      state.logs = action.payload.logs;
      state.backfilling = true;
      action.payload.operatorMessages.forEach((event) => {

        if (event.data.eventType === AlarmLogEventKind.OperatorChat) {

          dropOneOptimistic(state.optimisticOperator, event.data.message);

        }

      });

    })
    .addCase(fetchAlarmChat.rejected, (state, action) => {

      if (isAbortError(action.error) || action.meta.arg.controller.signal.aborted) return;

      state.status = AsyncStatus.Rejected;
      state.error = action.error;

    })
    .addCase(appendChat, (state, action) => {

      (Object.keys(action.payload) as (keyof typeof action.payload)[]).forEach((key) => {

        const known = new Set((state[key] as { _id: string }[]).map(({ _id }) => _id));
        const added = ((action.payload[key] ?? []) as { _id: string }[]).filter(({ _id }) => !known.has(_id));

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (state[key] as any[]).push(...added);

      });

    })
    .addCase(upsertAlarmCallParticipant, (state, action) => {

      // Keep a name we already know when the websocket sends the redacted version.
      const knownName = state.alarmCallParticipants.find(({ _id }) => _id === action.payload._id)?.name;

      upsertById(state.alarmCallParticipants, { ...action.payload, name: action.payload.name || knownName });

    })
    .addCase(finishBackfill, (state) => {

      state.backfilling = false;

    })
    .addCase(upsertCallEvent, (state, action) => {

      upsertById(state.callEvents, action.payload);

    })
    .addCase(removeCallEvent, (state, action) => {

      state.callEvents = state.callEvents.filter((event) => event._id !== action.payload);

    })
    .addCase(upsertAlarmCallEvent, (state, action) => {

      upsertById(state.alarmCallEvents, action.payload);

    })
    .addCase(removeAlarmCallEvent, (state, action) => {

      state.alarmCallEvents = state.alarmCallEvents.filter((event) => event._id !== action.payload);

    })
    .addCase(upsertParticipant, (state, action) => {

      // A redacted (blank) name from the socket must not wipe a name we already know.
      const knownName = state.participants.find(({ _id }) => _id === action.payload._id)?.name;

      upsertById(state.participants, { ...action.payload, name: action.payload.name || knownName });

    })
    .addCase(removeParticipant, (state, action) => {

      state.participants = state.participants.filter((participant) => participant._id !== action.payload);

    })
    .addCase(upsertLogEvent, (state, action) => {

      if (isOperatorChat(action.payload)) {

        upsertById(state.operatorMessages, action.payload);
        if (action.payload.data.eventType === AlarmLogEventKind.OperatorChat) {

          dropOneOptimistic(state.optimisticOperator, action.payload.data.message);

        }
        return;

      }

      upsertById(state.logs, action.payload);

    })
    .addCase(addCallId, (state, action) => {

      if (!state.callIds.includes(action.payload)) state.callIds.push(action.payload);
      if (!state.openHelperCallIds.includes(action.payload)) state.openHelperCallIds.push(action.payload);

    })
    .addCase(closeHelperCall, (state, action) => {

      state.openHelperCallIds = state.openHelperCallIds.filter((id) => id !== action.payload);

    })
    .addCase(appendOptimisticOperator, (state, action) => {

      state.optimisticOperator.unshift(action.payload);

    })
    .addCase(removeOptimisticOperator, (state, action) => {

      state.optimisticOperator = state.optimisticOperator.filter((row) => row._id !== action.payload);

    }),
});

export const stateSelector = (state: RS) => state.details.chat;

export const callIdsSelector = createSelector(stateSelector, (state) => state.callIds);

/** Participants of calls that are still open: someone on an ended call can never be connected. */
export const openCallParticipantsSelector = createSelector(
  stateSelector,
  ({ participants, openHelperCallIds }) => participants.filter(({ alarmCall }) => openHelperCallIds.includes(alarmCall._id)),
);

export { heartbeatMs };
